import { useTranslation } from 'react-i18next';
import { BrandIcon } from '../components/BrandIcon';

// Bumped manually whenever the wording on this page changes.
const LAST_UPDATED = '2026-05-19';

export function Datenschutz() {
  const { t } = useTranslation();

  // i18next returns `string | string[] | object` for nested values; we asked
  // for an array, so narrow it here.
  const dataItems = t('datenschutz.data.items', { returnObjects: true }) as string[];
  const cookieItems = t('datenschutz.cookies.items', { returnObjects: true }) as string[];

  return (
    <section aria-labelledby="datenschutz-title" className="max-w-2xl mx-auto">
      <div className="flex flex-col items-center text-center mb-8">
        <BrandIcon className="w-28 h-28 text-slate-900 dark:text-slate-100" />
        <h1
          id="datenschutz-title"
          className="mt-3 text-2xl sm:text-3xl font-semibold tracking-tight"
        >
          {t('datenschutz.title')}
        </h1>
        <p className="mt-2 text-xs text-slate-500 dark:text-slate-400 tabular-nums">
          {t('datenschutz.lastUpdated', { date: LAST_UPDATED })}
        </p>
      </div>

      <p className="text-sm text-slate-700 dark:text-slate-300 mb-4 leading-relaxed">
        {t('datenschutz.intro')}
      </p>

      <div className="bg-white border border-slate-200 dark:bg-slate-900 dark:border-slate-700 dark:divide-slate-700 rounded-lg divide-y divide-slate-200">
        <Section title={t('datenschutz.controller.title')}>
          <p className="leading-relaxed">{t('datenschutz.controller.body')}</p>
        </Section>

        <Section title={t('datenschutz.data.title')}>
          <p className="leading-relaxed">{t('datenschutz.data.body')}</p>
          <ul className="mt-2 space-y-1 list-disc pl-5">
            {dataItems.map((item, i) => (
              <li key={i}>{item}</li>
            ))}
          </ul>
        </Section>

        <Section title={t('datenschutz.purpose.title')}>
          <p className="leading-relaxed">{t('datenschutz.purpose.body')}</p>
        </Section>

        <Section title={t('datenschutz.legal.title')}>
          <p className="leading-relaxed">{t('datenschutz.legal.body')}</p>
        </Section>

        <Section title={t('datenschutz.thirdParties.title')}>
          <p className="leading-relaxed">{t('datenschutz.thirdParties.body')}</p>
        </Section>

        <Section title={t('datenschutz.retention.title')}>
          <p className="leading-relaxed">{t('datenschutz.retention.body')}</p>
        </Section>

        <Section title={t('datenschutz.cookies.title')}>
          <p className="leading-relaxed">{t('datenschutz.cookies.body')}</p>
          <ul className="mt-2 space-y-1 list-disc pl-5">
            {cookieItems.map((item, i) => (
              <li key={i}>{item}</li>
            ))}
          </ul>
        </Section>

        <Section title={t('datenschutz.rights.title')}>
          <p className="leading-relaxed">{t('datenschutz.rights.body')}</p>
        </Section>

        <Section title={t('datenschutz.security.title')}>
          <p className="leading-relaxed">{t('datenschutz.security.body')}</p>
        </Section>

        <Section title={t('datenschutz.changes.title')}>
          <p className="leading-relaxed">{t('datenschutz.changes.body')}</p>
        </Section>

        <Section title={t('datenschutz.contact.title')}>
          <p className="leading-relaxed">{t('datenschutz.contact.body')}</p>
          <p className="mt-1">
            <a
              href="mailto:datenschutz@baerenfang.ch"
              className="text-slate-800 dark:text-slate-200 underline decoration-dotted underline-offset-2 hover:text-slate-600 dark:hover:text-slate-400"
            >
              datenschutz@baerenfang.ch
            </a>
          </p>
        </Section>
      </div>
    </section>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="p-4 sm:p-6">
      <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-2">
        {title}
      </h2>
      <div className="text-sm text-slate-800 dark:text-slate-200">{children}</div>
    </div>
  );
}
